/**
 * Invites API (v0.10.0 - Issue #213)
 * 
 * Handles invite-only access control system.
 * Platform-agnostic - works on web and React Native.
 */

import { apiGet, apiPost, apiPatch } from '../api';

export interface InviteDetails {
  code: string;
  type: 'club_leader' | 'club_member';
  club: {
    id: string;
    name: string;
    logo_url: string | null;
  };
  inviter: {
    id: string;
    name: string;
  };
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expires_at: number | null;
}

export interface InviteDetailsResponse {
  invite: InviteDetails;
}

export interface AcceptInviteResponse {
  message: string;
  club_id: string;
  role: string;
  existing_role?: string;
}

export interface CreateInvitePayload {
  type: 'club_leader' | 'club_member';
  club_id: string;
  invitee_email?: string;
  invitee_phone?: string;
  expires_in_days?: number;
}

export interface InviteResponse {
  invite: {
    id: string;
    code: string;
    type: 'club_leader' | 'club_member';
    club_id: string;
    invitee_email: string | null;
    invitee_phone: string | null;
    status: string;
    expires_at: number | null;
    invite_url: string;
    created_at: number;
  };
}

export interface WaitlistSignupPayload {
  email?: string;
  phone?: string;
  source: 'homepage' | 'blocked_signup' | 'other';
  turnstile_token?: string;
}

export interface WaitlistSignupResponse {
  message: string;
  waitlist_id: string;
}

export interface Invite {
  id: string;
  code: string;
  type: string;
  club_id: string;
  invitee_email: string | null;
  invitee_phone: string | null;
  status: string;
  created_at: number;
  accepted_at: number | null;
  expires_at: number | null;
  club_name: string;
  inviter_first_name: string;
  inviter_last_name: string;
}

export interface WaitlistEntry {
  id: string;
  email: string | null;
  phone: string | null;
  source: string;
  created_at: number;
  invited: number;
  invited_at: number | null;
  notes: string | null;
}

export const invitesApi = {
  /**
   * Check invite validity (public)
   * GET /api/invites/:code
   */
  getInviteDetails: (code: string) => 
    apiGet<InviteDetailsResponse>(`/invites/${code}`),

  /**
   * Accept an invite (authenticated)
   * POST /api/invites/:code/accept
   */
  acceptInvite: (code: string) => 
    apiPost<AcceptInviteResponse>(`/invites/${code}/accept`, {}),

  /**
   * Join waitlist (public)
   * POST /api/invites/waitlist
   */
  joinWaitlist: (payload: WaitlistSignupPayload) => 
    apiPost<WaitlistSignupResponse>('/invites/waitlist', payload),

  /**
   * Create club member invite (club leader)
   * POST /api/clubs/:id/invites
   */
  createClubMemberInvite: (clubId: string, payload: Omit<CreateInvitePayload, 'club_id'>) => 
    apiPost<InviteResponse>(`/clubs/${clubId}/invites`, {
      ...payload,
      club_id: clubId,
    }),

  /**
   * Create club leader invite (admin)
   * POST /api/invites/admin/create
   */
  createClubLeaderInvite: (payload: CreateInvitePayload) => 
    apiPost<InviteResponse>('/invites/admin/create', payload),

  /**
   * List all invites (admin)
   * GET /api/invites/admin/list
   */
  listInvites: (params?: {
    status?: string;
    type?: string;
    club_id?: string;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.type) query.append('type', params.type);
    if (params?.club_id) query.append('club_id', params.club_id);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    return apiGet<{ invites: Invite[]; count: number; limit: number; offset: number }>(
      `/invites/admin/list?${query.toString()}`
    );
  },

  /**
   * View waitlist (admin)
   * GET /api/invites/admin/waitlist
   */
  viewWaitlist: (params?: {
    invited?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.invited !== undefined) query.append('invited', params.invited.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    return apiGet<{ waitlist: WaitlistEntry[]; count: number; limit: number; offset: number }>(
      `/invites/admin/waitlist?${query.toString()}`
    );
  },

  /**
   * Revoke an invite (admin)
   * PATCH /api/invites/admin/:id/revoke
   */
  revokeInvite: (inviteId: string) => 
    apiPatch<{ message: string }>(`/invites/admin/${inviteId}/revoke`, {}),
};
